// === Admin: Titles (Program Settings) ===
// Full CRUD page — list, add, edit, soft-delete. A title ticked "Do not count as
// part of licence" takes no plan seat: company staff whose job title has the
// same name aren't counted against the company's licence (staff or agronomist).
// Payroll Categories: the categories that apply to the title, each with the
// award / NES wording. A company job title of the same name shows only those
// on its Staff dialog, ticked by default, with the wording beside each.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import {
  PAY_HELP_PLACEHOLDERS,
  PAYROLL_CATEGORY_KINDS,
  PAYROLL_CATEGORY_LABELS,
  type PayrollCategoryKind,
  type TitlePayrollCategory,
} from '@click2gro/schema';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TitleRow {
  readonly id: number;
  readonly title: string;
  readonly excludes_from_licence: boolean;
  readonly no_sign_in: boolean;
  readonly include_with_new_companies: boolean;
  readonly payroll_categories: readonly TitlePayrollCategory[] | null;
  readonly hours_per_week: number | string | null;
  readonly base_hours: number | string | null;
  readonly standard_hours: number | string | null;
  readonly pay_help: string | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
  readonly created_by: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  title: string;
  excludes_from_licence: boolean;
  no_sign_in: boolean;
  include_with_new_companies: boolean;
  /** Ticked categories → their wording. Unticked kinds are absent. */
  payroll: Partial<Record<PayrollCategoryKind, string>>;
  /** Blank = set per person on the Staff dialog. */
  hours: Record<HoursKey, string>;
  /** Staff dialog Pay Help tab text; blank = no tab. */
  pay_help: string;
}

type HoursKey = 'hours_per_week' | 'base_hours' | 'standard_hours';

const HOURS_FIELDS: ReadonlyArray<{ key: HoursKey; label: string; hint: string }> = [
  { key: 'hours_per_week', label: 'Hours per week', hint: 'Ordinary hours in a week, e.g. 38' },
  { key: 'base_hours', label: 'Base hours', hint: 'Hours in a day before overtime, e.g. 8' },
  { key: 'standard_hours', label: 'Standard hours', hint: 'Ordinary paid hours in a day, e.g. 7.6' },
];

const EMPTY_HOURS: Record<HoursKey, string> = { hours_per_week: '', base_hours: '', standard_hours: '' };

const DEFAULT_FORM: FormState = {
  title: '', excludes_from_licence: false, no_sign_in: false, include_with_new_companies: false, payroll: {}, hours: EMPTY_HOURS, pay_help: '',
};

/** NUMERIC arrives as a string ("7.60") — show it as 7.6. */
const hoursText = (v: number | string | null): string => (v === null || v === '' ? '' : String(Number(v)));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  }).format(d);
}

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function UserTitlesBackendsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly TitleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [auditInfo, setAuditInfo] = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/user-titles-backends`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: TitleRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load titles');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setAuditInfo(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/user-titles-backends/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: TitleRow };
      setForm({
        title: json.data.title,
        excludes_from_licence: json.data.excludes_from_licence,
        no_sign_in: json.data.no_sign_in,
        include_with_new_companies: json.data.include_with_new_companies,
        payroll: Object.fromEntries((json.data.payroll_categories ?? []).map((p) => [p.kind, p.detail])),
        hours: {
          hours_per_week: hoursText(json.data.hours_per_week),
          base_hours: hoursText(json.data.base_hours),
          standard_hours: hoursText(json.data.standard_hours),
        },
        pay_help: json.data.pay_help ?? '',
      });
      setAuditInfo({
        created_at: json.data.created_at ?? null,
        updated_at: json.data.updated_at ?? null,
        created_by: json.data.created_by ?? null,
        updated_by: json.data.updated_by ?? null,
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    if (!form.title.trim()) { setFormError('Title is required.'); return; }
    const hours: Partial<Record<HoursKey, number | null>> = {};
    for (const f of HOURS_FIELDS) {
      const raw = form.hours[f.key].trim();
      const n = Number(raw);
      if (raw !== '' && (!Number.isFinite(n) || n < 0)) { setFormError(`${f.label} must be a number.`); return; }
      hours[f.key] = raw === '' ? null : n;
    }
    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/user-titles-backends/${editingId}`
        : `${getApiUrl()}/api/v1/admin/user-titles-backends`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          title: form.title.trim(),
          excludes_from_licence: form.excludes_from_licence,
          no_sign_in: form.no_sign_in,
          include_with_new_companies: form.include_with_new_companies,
          // Nothing ticked → null: the company chooses from every category.
          payroll_categories: PAYROLL_CATEGORY_KINDS
            .filter((k) => form.payroll[k] !== undefined)
            .map((k) => ({ kind: k, detail: form.payroll[k] ?? '' })),
          ...hours,
          pay_help: form.pay_help,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Title updated.' : 'Title added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/user-titles-backends/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Title deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Titles</Typography>
            <Typography variant="body2" color="text.secondary">
              Job titles that don&apos;t count as part of a company&apos;s licence, and titles added to every new company. Matched to company job titles by name.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Title
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search titles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>Clear</Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Title</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 200 }}>Licence</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 200 }}>New Companies</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 200 }}>Payroll</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.title}</TableCell>
                      <TableCell>
                        {row.excludes_from_licence
                          ? <Chip size="small" color="success" variant="outlined" label="Does not count" />
                          : <Chip size="small" variant="outlined" label="Counts" />}
                        {row.no_sign_in ? <Chip size="small" color="warning" variant="outlined" label="No sign-in" sx={{ ml: 0.5 }} /> : null}
                      </TableCell>
                      <TableCell>
                        {row.include_with_new_companies
                          ? <Chip size="small" color="primary" variant="outlined" label="Included" />
                          : <Typography variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell>
                        {row.payroll_categories !== null && row.payroll_categories.length > 0
                          ? <Chip size="small" variant="outlined" label={`${row.payroll_categories.length} categories`} />
                          : <Typography variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, row.title)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found' :
                `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`
              }
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{editingId ? 'Edit Title' : 'Add Title'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Title"
            size="small"
            fullWidth
            required
            placeholder="e.g. CASUAL PICKER"
            inputProps={{ maxLength: 200 }}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            helperText="Must match the company's job title name (not case-sensitive)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.excludes_from_licence}
                onChange={(e) => setForm({ ...form, excludes_from_licence: e.target.checked })}
              />
            }
            label="Do not count as part of licence"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.no_sign_in}
                onChange={(e) => setForm({ ...form, no_sign_in: e.target.checked })}
              />
            }
            label="No portal access — staff with this title are not permitted to sign in or use the portal (e.g. labourers: no email or password; still selectable as labour and paid via the Timesheet)"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.include_with_new_companies}
                onChange={(e) => setForm({ ...form, include_with_new_companies: e.target.checked })}
              />
            }
            label="Include with new companies"
          />

          {/* Pay settings — fixed for staff on this title; blank = per person */}
          <Box>
            <Typography variant="subtitle2">Pay Settings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Read-only on the company&apos;s Staff screen for staff on this job title. Leave blank to let the
              company set it per person (e.g. a part-timer&apos;s agreed hours per week).
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
              {HOURS_FIELDS.map((f) => (
                <TextField
                  key={f.key}
                  label={f.label}
                  size="small"
                  fullWidth
                  helperText={f.hint}
                  inputProps={{ inputMode: 'decimal' }}
                  value={form.hours[f.key]}
                  onChange={(e) => setForm({ ...form, hours: { ...form.hours, [f.key]: e.target.value } })}
                />
              ))}
            </Box>
          </Box>

          {/* Payroll Categories — category | regulation wording */}
          <Box>
            <Typography variant="subtitle2">Payroll Categories</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Tick the categories this kind of worker is paid for and write what the award says about each.
              Company staff on this job title see only these, with this wording. Leave all unticked to let the
              company choose from every category.
            </Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '240px 1fr', px: 1.5, py: 1, bgcolor: 'action.hover' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary">CATEGORY</Typography>
                <Typography variant="caption" fontWeight={700} color="text.secondary">REGULATION</Typography>
              </Box>
              {PAYROLL_CATEGORY_KINDS.map((kind) => {
                const ticked = form.payroll[kind] !== undefined;
                return (
                  <Box
                    key={kind}
                    sx={{ display: 'grid', gridTemplateColumns: '240px 1fr', alignItems: 'center', columnGap: 2, px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={ticked}
                          onChange={(e) => {
                            const payroll = { ...form.payroll };
                            if (e.target.checked) payroll[kind] = '';
                            else delete payroll[kind];
                            setForm({ ...form, payroll });
                          }}
                        />
                      }
                      label={PAYROLL_CATEGORY_LABELS[kind]}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      minRows={1}
                      disabled={!ticked}
                      placeholder={ticked ? 'e.g. 150% for overtime Monday to Saturday (Horticulture Award cl 21.3)' : ''}
                      inputProps={{ maxLength: 2000 }}
                      value={form.payroll[kind] ?? ''}
                      onChange={(e) => setForm({ ...form, payroll: { ...form.payroll, [kind]: e.target.value } })}
                    />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Pay Help — the Staff dialog's Pay Help tab, with {placeholders} */}
          <Box>
            <Typography variant="subtitle2">Pay Help</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Shown on the company&apos;s Staff screen as a Pay Help tab for staff on this job title. Leave blank for
              no tab. Blank line = new paragraph · &quot;1. &quot; numbered list · &quot;- &quot; bullets ·
              **bold** · &quot;| a | b |&quot; table rows (first row is the header).
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={24}
              inputProps={{ maxLength: 20000 }}
              value={form.pay_help}
              onChange={(e) => setForm({ ...form, pay_help: e.target.value })}
              sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Filled with each person&apos;s own pay settings (&quot;—&quot; when not set):{' '}
              {PAY_HELP_PLACEHOLDERS.map((p, i) => (
                <span key={p.key}>{i > 0 ? ' · ' : ''}<code>{`{${p.key}}`}</code> {p.meaning}</span>
              ))}
            </Typography>
          </Box>

          {/* Audit Trail */}
          {editingId && auditInfo && (
            <Box
              mt={1}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Title'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Title</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? Staff on a company job title with this name will count toward the licence again.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
